import React, { useEffect, useState, useCallback } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasSuperPrivileges } from "@/lib/accountRole";
import { LoadFailed } from "@/components/LoadState";
import { Crosshair } from "@/components/Crosshair";
import { toast } from "@/lib/toast";
import { Users, UserCheck, Hourglass, DoorOpen, ListChecks, RotateCcw, Lock, Download, AlertTriangle, Ticket, ShieldCheck } from "lucide-react";

const Tile = ({ icon: Icon, label, value, sub, testid, tone = "olive" }) => {
  const color = tone === "blaze" ? "#C2600F" : tone === "gold" ? "#E8C46A" : "#8FA84A";
  return (
    <div className="border border-sniper bg-surface p-4" data-testid={testid}>
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        <Icon className="w-3.5 h-3.5" style={{ color }} /> {label}
      </div>
      <div className="mt-1.5 font-heading text-3xl font-bold" style={{ color }}>{value}</div>
      {sub && <div className="mt-0.5 text-[11px] font-body text-secondary-foreground">{sub}</div>}
    </div>
  );
};

export default function AdminMembershipCap() {
  const { user } = useAuth();
  const superAdmin = hasSuperPrivileges(user?.role);
  const [data, setData] = useState(null);
  const [waitlist, setWaitlist] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [alerts, setAlerts] = useState({ banners: [], derived: [] });
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const [capInput, setCapInput] = useState("");
  const [reason, setReason] = useState("");
  const [ovEmail, setOvEmail] = useState("");
  const [ovReason, setOvReason] = useState("");

  const load = useCallback(async () => {
    try {
      const [r, w, o, a] = await Promise.all([
        api.get("/admin/membership/cap"),
        api.get("/admin/membership/waitlist"),
        api.get("/admin/membership/overrides"),
        api.get("/admin/membership/alerts"),
      ]);
      setData(r.data);
      setCapInput(String(r.data.cap_value));
      setWaitlist(w.data.waitlist || []);
      setOverrides(o.data.overrides || []);
      setAlerts(a.data || { banners: [], derived: [] });
      setErr(false);
    } catch (e) { setErr(true); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveCap = async (patch, why) => {
    if (!superAdmin) return;
    setBusy(true);
    try {
      await api.put("/admin/membership/cap", { ...patch, reason: why || reason || "cap update" });
      toast.success("Membership cap updated.");
      setReason("");
      await load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleEnabled = () => {
    if (!superAdmin) return;
    const next = !data.cap_enabled;
    saveCap({ cap_enabled: next }, next ? "enable membership cap" : "disable membership cap");
  };

  const saveValue = () => {
    const v = parseInt(capInput, 10);
    if (!(v >= 1)) { toast.error("Enter a cap of 1 or more."); return; }
    if (v === data.cap_value) { toast.info("Cap value unchanged."); return; }
    if ((reason || "").trim().length < 5) { toast.error("Add a short reason (5+ chars) for the audit log."); return; }
    saveCap({ cap_value: v });
  };

  const admit = async (row) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/admin/membership/waitlist/${row.waitlist_id}/admit`);
      toast.success(`Admitted ${res.email} — one-time override expires ${new Date(res.override_expires_at).toLocaleDateString()}.`);
      await load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const grantOverride = async () => {
    if (!ovEmail.includes("@")) { toast.error("Enter a valid email."); return; }
    if ((ovReason || "").trim().length < 3) { toast.error("Add a reason."); return; }
    setBusy(true);
    try {
      await api.post("/admin/membership/override", { email: ovEmail.trim(), reason: ovReason.trim() });
      toast.success("Override granted.");
      setOvEmail(""); setOvReason("");
      await load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const dismissBanner = async (id) => {
    try { await api.post(`/admin/membership/alerts/${id}/dismiss`); await load(); } catch (e) {}
  };

  const exportCsv = () => {
    const rows = [["email", "name", "how_heard", "status", "created_at"],
      ...waitlist.map((w) => [w.email, w.name || "", w.how_heard || "", w.status || "", w.created_at || ""])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "membership_waitlist.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  if (err) return <div className="p-6"><LoadFailed onRetry={load} /></div>;
  if (!data) return <div className="p-6 font-mono text-sm text-muted-foreground">Loading…</div>;

  const inputCls = "w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-mono text-sm";

  return (
    <div className="p-6 max-w-6xl" data-testid="admin-membership-cap">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">Membership Cap</h1>
          <p className="text-sm text-secondary-foreground font-body mt-1">
            Pause new memberships at a ceiling. A seat counts only when a member is <span className="text-foreground">ID-verified</span> — independent of the founding-seat pool.
          </p>
        </div>
        <span data-testid="cap-enabled-chip" className={`font-mono text-xs uppercase px-3 py-1.5 border ${data.cap_enabled ? "border-[#C2600F] text-[#E8C46A] bg-[#C2600F]/10" : "border-sniper text-muted-foreground"}`}>
          {data.cap_enabled ? "Cap ON — enforcing" : "Cap OFF — not enforcing"}
        </span>
      </div>

      {/* Persistent alert banners (fire-once thresholds) + derived attention flags */}
      {(alerts.banners.length > 0 || alerts.derived.length > 0) && (
        <div className="mt-5 space-y-2" data-testid="cap-alert-banners">
          {alerts.banners.map((b) => (
            <div key={b.alert_id} data-testid={`cap-banner-${b.alert_key}`} className="flex items-start justify-between gap-3 border border-[#C2600F]/50 bg-[#C2600F]/10 px-4 py-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-[#E8C46A] mt-0.5 shrink-0" />
                <div><p className="font-heading uppercase text-sm tracking-tight text-[#E8C46A]">{b.title}</p>
                  <p className="text-[13px] font-body text-secondary-foreground whitespace-pre-line mt-0.5">{b.body}</p></div>
              </div>
              <button onClick={() => dismissBanner(b.alert_id)} data-testid={`cap-banner-dismiss-${b.alert_key}`} className="font-mono text-[10px] uppercase text-muted-foreground hover:text-foreground border border-sniper px-2 py-1">Dismiss</button>
            </div>
          ))}
          {alerts.derived.map((d) => (
            <div key={d.key} data-testid={`cap-flag-${d.key}`} className="flex items-start gap-2 border border-olive/40 bg-olive/5 px-4 py-3">
              <ShieldCheck className="w-4 h-4 text-olive-bright mt-0.5 shrink-0" />
              <div><p className="font-heading uppercase text-sm tracking-tight text-olive-bright">{d.title}</p>
                <p className="text-[13px] font-body text-secondary-foreground mt-0.5">{d.body}</p></div>
            </div>
          ))}
        </div>
      )}

      {/* Live readout */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="cap-readout">
        <Tile icon={UserCheck} label="Verified members" value={data.verified} testid="cap-tile-verified" />
        <Tile icon={Hourglass} label="Pending (holding)" value={data.pending_holding} sub={`≤ ${data.reservation_days}d reservation`} testid="cap-tile-pending" />
        <Tile icon={DoorOpen} label="Seats remaining" value={data.seats_remaining} sub={`of ${data.cap_value}`} testid="cap-tile-remaining" />
        <Tile icon={Ticket} label="Waitlist" value={data.waitlist_count} testid="cap-tile-waitlist" />
        <Tile icon={RotateCcw} label="Reclaimable" value={data.reclaimable} sub="blocked / dormant" testid="cap-tile-reclaimable" tone="gold" />
        <Tile icon={Users} label="Overshoot" value={data.overshoot} sub={data.overshoot > 0 ? "over cap (in-flight)" : "none"} testid="cap-tile-overshoot" tone={data.overshoot > 0 ? "blaze" : "olive"} />
      </div>
      <p className="mt-2 text-[11px] font-mono text-muted-foreground">
        Thresholds fire at {data.thresholds.pct90} / {data.thresholds.pct95} / {data.thresholds.pct100} verified. {data.environment_note}
      </p>

      <Crosshair className="my-6" />

      {/* Controls */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="border border-sniper bg-surface p-5" data-testid="cap-controls">
          <h2 className="font-heading text-lg font-bold uppercase tracking-tight flex items-center gap-2"><ListChecks className="w-5 h-5 text-olive" /> Cap settings</h2>
          {!superAdmin && (
            <p className="mt-2 flex items-center gap-1.5 text-[12px] font-mono uppercase text-muted-foreground" data-testid="cap-super-note"><Lock className="w-3.5 h-3.5" /> Editing the cap requires super admin. Admitting people (below) is fine as admin.</p>
          )}
          <div className="mt-4 flex items-center justify-between border border-sniper bg-obsidian/40 px-4 py-3">
            <div><p className="font-heading uppercase text-sm">Cap enforcement</p>
              <p className="text-[12px] font-body text-secondary-foreground">Turn the pause on or off without changing the number.</p></div>
            <button onClick={toggleEnabled} disabled={!superAdmin || busy} data-testid="cap-toggle"
              className={`font-mono text-xs uppercase px-4 py-2 border transition-colors disabled:opacity-40 ${data.cap_enabled ? "border-[#C2600F] text-[#E8C46A] bg-[#C2600F]/10" : "border-olive text-olive-bright hover:bg-olive/10"}`}>
              {data.cap_enabled ? "Turn OFF" : "Turn ON"}
            </button>
          </div>
          <div className="mt-4">
            <label className="font-mono text-xs uppercase tracking-wide text-secondary-foreground block mb-1">Cap value</label>
            <div className="flex gap-2">
              <input type="number" min="1" value={capInput} onChange={(e) => setCapInput(e.target.value)} disabled={!superAdmin} className={inputCls} data-testid="cap-value-input" />
              <button onClick={saveValue} disabled={!superAdmin || busy} data-testid="cap-value-save" className="shrink-0 bg-olive hover:bg-olive-bright disabled:opacity-40 text-white font-heading font-bold uppercase text-xs px-4 transition-colors">Save</button>
            </div>
            <input value={reason} onChange={(e) => setReason(e.target.value)} disabled={!superAdmin} placeholder="Reason for the change (audit log)" className={`${inputCls} mt-2`} data-testid="cap-reason-input" />
            <p className="mt-2 text-[11px] font-body text-muted-foreground">Changing the value resets the 90/95/100% alerts and re-proportions them to the new cap.</p>
          </div>
        </div>

        <div className="border border-sniper bg-surface p-5" data-testid="cap-override-panel">
          <h2 className="font-heading text-lg font-bold uppercase tracking-tight flex items-center gap-2"><Ticket className="w-5 h-5 text-olive" /> Grant an override</h2>
          <p className="mt-2 text-[12px] font-body text-secondary-foreground">Let a specific person register past the cap (e.g. an invited person). They still create their own account and verify their own ID — an override is not a verification.</p>
          <input value={ovEmail} onChange={(e) => setOvEmail(e.target.value)} placeholder="email@example.com" className={`${inputCls} mt-3`} data-testid="override-email-input" />
          <input value={ovReason} onChange={(e) => setOvReason(e.target.value)} placeholder="Reason" className={`${inputCls} mt-2`} data-testid="override-reason-input" />
          <button onClick={grantOverride} disabled={busy} data-testid="override-grant-btn" className="mt-2 bg-olive hover:bg-olive-bright disabled:opacity-40 text-white font-heading font-bold uppercase text-xs px-4 py-2 transition-colors">Grant override</button>
          {overrides.length > 0 && (
            <div className="mt-4 border-t border-sniper pt-3 max-h-40 overflow-auto" data-testid="override-list">
              {overrides.map((o) => (
                <div key={o.email} className="flex items-center justify-between py-1 font-mono text-[11px]">
                  <span className="text-foreground truncate">{o.email}</span>
                  <span className={o.used ? "text-muted-foreground" : "text-olive-bright"}>{o.used ? "used" : "active"} · {o.source} · by {o.granted_by}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Crosshair className="my-6" />

      {/* Waitlist */}
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-lg font-bold uppercase tracking-tight">Waitlist <span className="text-muted-foreground">({waitlist.length})</span></h2>
        <button onClick={exportCsv} disabled={!waitlist.length} data-testid="waitlist-export-btn" className="inline-flex items-center gap-1.5 border border-sniper hover:border-olive text-secondary-foreground hover:text-foreground font-mono uppercase text-xs px-3 py-1.5 transition-colors disabled:opacity-40">
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>
      {waitlist.length === 0 ? (
        <p className="mt-3 text-sm font-body text-muted-foreground border border-dashed border-sniper p-4" data-testid="waitlist-empty">No one is on the waitlist. When the cap is on and full, new signups land here.</p>
      ) : (
        <div className="mt-3 border border-sniper overflow-hidden" data-testid="waitlist-table">
          <table className="w-full text-sm">
            <thead className="bg-obsidian/60 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr><th className="text-left px-3 py-2">Email</th><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2 hidden sm:table-cell">How heard</th><th className="text-left px-3 py-2">Status</th><th className="text-right px-3 py-2">Action</th></tr>
            </thead>
            <tbody>
              {waitlist.map((w) => (
                <tr key={w.waitlist_id} className="border-t border-sniper" data-testid={`waitlist-row-${w.waitlist_id}`}>
                  <td className="px-3 py-2 text-foreground">{w.email}</td>
                  <td className="px-3 py-2 text-secondary-foreground">{w.name || "—"}</td>
                  <td className="px-3 py-2 text-secondary-foreground hidden sm:table-cell">{w.how_heard || "—"}</td>
                  <td className="px-3 py-2"><span className={`font-mono text-[10px] uppercase ${w.admitted ? "text-olive-bright" : "text-muted-foreground"}`}>{w.status}</span></td>
                  <td className="px-3 py-2 text-right">
                    {w.admitted
                      ? <span className="font-mono text-[10px] uppercase text-olive-bright" data-testid={`waitlist-admitted-${w.waitlist_id}`}>Admitted</span>
                      : <button onClick={() => admit(w)} disabled={busy} data-testid={`waitlist-admit-${w.waitlist_id}`} className="font-mono text-[10px] uppercase border border-olive text-olive-bright hover:bg-olive/10 px-3 py-1 transition-colors disabled:opacity-40">Admit</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
